import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { arrayMove } from "@dnd-kit/sortable";
import { Flex, Text } from "styles/utility-components";

import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { DeleteStageModal } from "./DeleteStageModal";
import {
  createCustomiseWorkflowInput,
  CustomiseWorkflowInput,
  getGroupIcon,
  groupWorkflowTransitions,
  NewTransition,
  StageGroup,
  transitionToInput,
  Workflow,
  WorkflowFormValues,
} from "./utils";
import WorkflowGroupEditor from "./WorkflowGroupEditor";

type DeleteModalState = { reason: "hasItems"; count: number } | { reason: "lastInGroup" };

type WorkflowContainerProps = {
  workflow?: Workflow;
  onWorkflowChange: (input: CustomiseWorkflowInput) => void;
  isError: boolean;
  isLoading: boolean;
  title: string;
  workflowType: "bid" | "lead";
  generateSearchUrl: (stageId: string) => string;
};

export default function WorkflowContainer({
  workflow,
  onWorkflowChange,
  isLoading,
  isError,
  title,
  workflowType,
  generateSearchUrl,
}: WorkflowContainerProps): JSX.Element {
  const [deleteModal, setDeleteModal] = useState<DeleteModalState | null>(null);

  // groupedWorkflow is used for display and guards (stage.count, iconName, etc.)
  const groupedWorkflow = useMemo(() => groupWorkflowTransitions(workflow), [workflow]);

  const methods = useForm<WorkflowFormValues>({
    defaultValues: {
      transitions: [],
    },
  });
  const { watch, reset, control } = methods;
  const { replace } = useFieldArray({ control, name: "transitions", keyName: "key" });

  // Sync form with server-confirmed data after any mutation completes.
  useEffect(() => {
    if (!workflow) return;

    // Sort probably isn't necessary - but just in case
    reset({
      transitions: [...workflow.transitions]
        .map(transitionToInput)
        .sort((a, b) => a.order - b.order),
    });
  }, [workflow, reset]);

  // Transitions that are in the form - may be out of sync with the API fetched transitions due to user updates
  const currentTransitions = watch("transitions");

  const onDeleteStage = useCallback(
    (group: StageGroup, stageId: string) => {
      if (!groupedWorkflow) return;
      const transition = group.transitions.find((t) => t.stage.id === stageId);
      if (!transition) return;

      if (transition.stage.count > 0) {
        setDeleteModal({ reason: "hasItems", count: transition.stage.count });
        return;
      }
      if (group.transitions.length === 1) {
        setDeleteModal({ reason: "lastInGroup" });
        return;
      }

      const deletedTransition = currentTransitions.find((t) => t.stageId === stageId);
      if (!deletedTransition) return;
      const deletedOrder = deletedTransition.order;
      const newTransitions = currentTransitions
        .filter((t) => t.stageId !== stageId)
        .map((t) => ({ ...t, order: t.order > deletedOrder ? t.order - 1 : t.order }));
      replace(newTransitions);

      onWorkflowChange(createCustomiseWorkflowInput(groupedWorkflow, newTransitions));
    },
    [groupedWorkflow, currentTransitions, replace, onWorkflowChange],
  );

  const onEditStage = useCallback(
    (stageId: string, newStageName: string, iconColor: string, backgroundColor: string) => {
      if (!groupedWorkflow || !newStageName.trim()) return;
      const newTransitions = currentTransitions.map((t) =>
        t.stageId !== stageId
          ? t
          : {
              ...t,
              stageName: newStageName.trim(),
              stageColor: backgroundColor,
              stageIconColor: iconColor,
            },
      );
      replace(newTransitions);

      onWorkflowChange(createCustomiseWorkflowInput(groupedWorkflow, newTransitions));
    },
    [groupedWorkflow, currentTransitions, replace, onWorkflowChange],
  );

  const onCreateStage = useCallback(
    (group: StageGroup, newStageName: string, iconColor: string, backgroundColor: string) => {
      if (!groupedWorkflow || !newStageName.trim()) return;
      const stageIconName = getGroupIcon(group);
      const groupTransitions = currentTransitions.filter((t) => t.group === group.name);
      const maxOrderInGroup =
        groupTransitions.length > 0 ? Math.max(...groupTransitions.map((t) => t.order)) : 0;
      const newOrder = maxOrderInGroup + 1;

      const newTransition: NewTransition = {
        stageName: newStageName.trim(),
        stageColor: backgroundColor,
        stageIconColor: iconColor,
        stageIconName,
        order: newOrder,
        group: group.name,
      };

      const newTransitions = [
        ...currentTransitions.map((t) => ({
          ...t,
          order: t.order >= newOrder ? t.order + 1 : t.order,
        })),
        newTransition,
      ].sort((a, b) => a.order - b.order);
      replace(newTransitions);

      onWorkflowChange(createCustomiseWorkflowInput(groupedWorkflow, newTransitions));
    },
    [groupedWorkflow, currentTransitions, replace, onWorkflowChange],
  );

  const onReorderStages = useCallback(
    (activeTransitionId: string, overTransitionId: string) => {
      if (!groupedWorkflow) return;
      // The form holds a flat array sorted by order, so we can arrayMove directly.
      // Active and over are always within the same group so no cross-group movement occurs.

      const activeIdx = currentTransitions.findIndex((t) => t.id === activeTransitionId);
      const overIdx = currentTransitions.findIndex((t) => t.id === overTransitionId);
      if (activeIdx === -1 || overIdx === -1) return;

      const newTransitions = arrayMove([...currentTransitions], activeIdx, overIdx).map((t, i) => ({
        ...t,
        order: i + 1,
      }));
      replace(newTransitions);
      onWorkflowChange(createCustomiseWorkflowInput(groupedWorkflow, newTransitions));
    },
    [groupedWorkflow, currentTransitions, replace, onWorkflowChange],
  );

  if (isLoading) {
    return <CentredSpinner label="Loading workflow" />;
  }

  if (isError) {
    return (
      <EmptyState
        heading="Sorry, something has gone wrong"
        description="If the error persists, please contact support"
        type="loadError"
        size="large"
      />
    );
  }

  if (!groupedWorkflow) {
    return (
      <EmptyState heading={`No ${workflowType} workflow found`} type="noResults" size="large" />
    );
  }

  return (
    <FormProvider {...methods}>
      <Flex column gap={48} width="100%" alignItems="stretch">
        <Text h1>{title}</Text>
        <Flex column gap={16} aria-label="Workflow stages">
          {groupedWorkflow.groups.map((group) => (
            <WorkflowGroupEditor
              key={group.name}
              group={group}
              workflowType={workflowType}
              generateSearchUrl={generateSearchUrl}
              onDeleteStage={onDeleteStage}
              onCreateStage={onCreateStage}
              onEditStage={onEditStage}
              onReorderStages={onReorderStages}
            />
          ))}
          {deleteModal && (
            <DeleteStageModal
              {...deleteModal}
              isOpen={!!deleteModal}
              onClose={() => setDeleteModal(null)}
              workflowType={workflowType}
            />
          )}
        </Flex>
      </Flex>
    </FormProvider>
  );
}
